import type { ReactElement } from 'react';
import React, { useMemo } from 'react';
import type { Props as BaseProps } from 'feature-components/assessment-filters/base-filter';
import BaseFilter from 'feature-components/assessment-filters/base-filter';
import type { BaseLevel } from 'services/assist-api/entities/level/levels';
import { useLevels } from 'services/assist-api/entities/level/levels';

export type Props<IsMulti extends boolean> = {
  enabledIds?: number[];
  filteredIds?: number[];
  label?: BaseProps<BaseLevel, IsMulti>['label'];
} & Pick<
  BaseProps<BaseLevel, IsMulti>,
  'isMulti' | 'isClearable' | 'isSearchable' | 'value' | 'onChange'
>;

/**
 * Filter that is auto populated by backend defined levels
 *
 *  - fetches levels
 *  - has loading state until levels are fetched
 *  - can only show levels with enabledIds
 *  - by default filters out levels disabled on the endpoint (unless included in enabledIds)
 */
export default function YearLevelFilter<IsMulti extends boolean = false>({
  enabledIds,
  label = 'Year Level',
  isClearable = true,
  isSearchable = true,
  ...props
}: Props<IsMulti>): ReactElement {
  const levelOptions = useLevels().data?.json;

  // Only include options that are enabled in the backed or the component requests
  const filteredOptions = useMemo(
    () => levelOptions?.filter((o) => (enabledIds?.length ? enabledIds.includes(o.id) : o.enabled)),
    [enabledIds, levelOptions]
  );

  return (
    <BaseFilter<BaseLevel, IsMulti>
      label={label}
      options={filteredOptions || []}
      getOptionValue={(o): string => o.value}
      getOptionLabel={(o): string => o.name}
      isLoading={!levelOptions?.length}
      isClearable={isClearable}
      isSearchable={isSearchable}
      {...props}
    />
  );
}
